import React from 'react';

/**
 * Unified SVG icon set – monochrome, stroke-based, uses currentColor
 * so the icons inherit colour from their parent element / CSS.
 *
 * Every icon accepts an optional `size` (default 18) and any standard
 * SVG / HTML props.
 */

interface IconProps extends React.SVGAttributes<SVGSVGElement> {
  size?: number;
}

const defaults = (size: number, props: React.SVGAttributes<SVGSVGElement>) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  ...props,
});

/* ── Gradient paint source ─────────────────────────────────────────
 * The icons below stroke with `currentColor`, which can only ever be a
 * flat colour. To paint them with the brand violet→cyan gradient we
 * render the <linearGradient> once per document and let the
 * `.icon-gradient` class (App.css) point `stroke` at it by id.
 *
 * Mount this once, high in the tree — ProjectSidebarLayout already does.
 */
export const IconGradientDefs: React.FC = () => (
  <svg
    width="0"
    height="0"
    aria-hidden="true"
    focusable="false"
    style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
  >
    <defs>
      {/* userSpaceOnUse, spanning the shared 0 0 24 24 viewBox. The default
          objectBoundingBox units would resolve against each *path's* own
          bounding box, and a bbox with zero width or height (every straight
          <line> inside these icons) makes the gradient undefined — the
          segment then vanishes. */}
      <linearGradient
        id="vinaIconGradient"
        gradientUnits="userSpaceOnUse"
        x1="0"
        y1="0"
        x2="24"
        y2="24"
      >
        <stop offset="0%" stopColor="#8B5CF6" />
        <stop offset="55%" stopColor="#4F7DF3" />
        <stop offset="100%" stopColor="#22C7E8" />
      </linearGradient>
    </defs>
  </svg>
);

/* ── Sidebar icons ─────────────────────────────────────────────── */

export const IconAnalytics: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M3 3v18h18" />
    <path d="M7 16l4-8 4 4 5-9" />
  </svg>
);

export const IconWorkspace: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

export const IconAgent: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <circle cx="9" cy="16" r="1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="16" r="1" fill="currentColor" stroke="none" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    <path d="M12 3v1" />
  </svg>
);

export const IconKnowledge: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    <line x1="9" y1="7" x2="17" y2="7" />
    <line x1="9" y1="11" x2="15" y2="11" />
  </svg>
);

export const IconWebChat: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <line x1="8" y1="9" x2="16" y2="9" />
    <line x1="8" y1="13" x2="13" y2="13" />
  </svg>
);

export const IconWhatsApp: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

export const IconVoice: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

export const IconConversations: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

export const IconSettings: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

export const IconUsers: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

export const IconSecurity: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

export const IconBilling: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

export const IconTools: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

export const IconSupport: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="4" />
    <line x1="4.93" y1="4.93" x2="9.17" y2="9.17" />
    <line x1="14.83" y1="14.83" x2="19.07" y2="19.07" />
    <line x1="14.83" y1="9.17" x2="19.07" y2="4.93" />
    <line x1="4.93" y1="19.07" x2="9.17" y2="14.83" />
  </svg>
);

export const IconEscalations: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
    <line x1="12" y1="2" x2="12" y2="22" />
  </svg>
);

export const IconLogs: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="16" y2="17" />
  </svg>
);

export const IconProfile: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

export const IconLogout: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

/* ── Mockup-matched nav icons ──────────────────────────────────── */

/** Dashboard — 2×2 rounded tiles. */
export const IconGrid: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <rect x="3" y="3" width="7.5" height="7.5" rx="2" />
    <rect x="13.5" y="3" width="7.5" height="7.5" rx="2" />
    <rect x="3" y="13.5" width="7.5" height="7.5" rx="2" />
    <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="2" />
  </svg>
);

/** Agent (AI Builder) — wand with sparkles. */
export const IconWand: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M21.64 3.64l-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72z" />
    <path d="M14 7l3 3" />
    <path d="M5 6v4" />
    <path d="M19 14v4" />
    <path d="M10 2v2" />
    <path d="M7 8H3" />
    <path d="M21 16h-4" />
    <path d="M11 3H9" />
  </svg>
);

/** Actions — four-point sparkle. */
export const IconSparkle: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M9.94 15.5A2 2 0 0 0 8.5 14.06l-6.14-1.58a.5.5 0 0 1 0-.96L8.5 9.94A2 2 0 0 0 9.94 8.5l1.58-6.14a.5.5 0 0 1 .96 0l1.58 6.14a2 2 0 0 0 1.44 1.44l6.13 1.58a.5.5 0 0 1 0 .96l-6.13 1.58a2 2 0 0 0-1.44 1.44l-1.58 6.14a.5.5 0 0 1-.96 0z" />
  </svg>
);

/** Automations — orbit. */
export const IconAutomation: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M20.34 6.48A10 10 0 0 1 10.27 21.85" />
    <path d="M3.66 17.52A10 10 0 0 1 13.74 2.15" />
    <circle cx="12" cy="12" r="3" />
    <circle cx="19" cy="5" r="2" />
    <circle cx="5" cy="19" r="2" />
  </svg>
);

/** Support Tickets — perforated ticket. */
export const IconTicket: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" />
    <path d="M13 5v2" />
    <path d="M13 11v2" />
    <path d="M13 17v2" />
  </svg>
);

/* ── Dashboard KPI card icons ──────────────────────────────────── */

/** Total Conversations — dollar sign. */
export const IconDollar: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <line x1="12" y1="2" x2="12" y2="22" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

/** Total Messages — support headset. */
export const IconHeadset: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M3 12a9 9 0 0 1 18 0" />
    <path d="M3 12v5a2 2 0 0 0 2 2h1a1 1 0 0 0 1-1v-5a1 1 0 0 0-1-1H3z" />
    <path d="M21 12v5a2 2 0 0 1-2 2h-1a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1h3z" />
  </svg>
);

/** Avg Session Duration — eye. */
export const IconEye: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

/* ── Dashboard / Analytics icons ───────────────────────────────── */

export const IconMessages: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H8l-4 4V6c0-1.1.9-2 2-2z" />
    <line x1="8" y1="9" x2="16" y2="9" />
    <line x1="8" y1="13" x2="12" y2="13" />
  </svg>
);

export const IconZap: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

export const IconClock: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

export const IconActivity: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

export const IconTrendingUp: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

export const IconRadio: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <circle cx="12" cy="12" r="2" />
    <path d="M16.24 7.76a6 6 0 0 1 0 8.49" />
    <path d="M7.76 16.24a6 6 0 0 1 0-8.49" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    <path d="M4.93 19.07a10 10 0 0 1 0-14.14" />
  </svg>
);

export const IconPhone: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

export const IconBarChart: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

export const IconStar: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

export const IconRefresh: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

export const IconAlertTriangle: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

export const IconFileText: React.FC<IconProps> = ({ size = 18, ...p }) => (
  <svg {...defaults(size, p)}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="16" y2="17" />
    <line x1="10" y1="9" x2="14" y2="9" />
  </svg>
);
